import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import { AlarmOutlined, HistoryOutlined } from "@mui/icons-material";
import useErrorHandler from "../../hooks/useErrorHandler";
import { getDeadlines } from "../../api/deadlines";
import { AddDeadlineDialog, AuditDialog, CompleteDialog, ExtendDialog, fmtDate } from "./deadline-dialogs";
import RulesAndHolidays from "./rules-holidays";

const STATUS = {
  OVERDUE: { label: "Overdue", color: "#c62828", tint: "#fde8e8" },
  DUE_SOON: { label: "Due within 3 days", color: "#b45309", tint: "#fff3dc" },
  ON_TRACK: { label: "On track", color: "#1b8a5a", tint: "#e7f6ee" },
  COMPLETED: { label: "Completed", color: "#6b7a90", tint: "#eceff4" },
};

export function daysChip(d) {
  const s = STATUS[d.status] || STATUS.ON_TRACK;
  let label = "Completed";
  if (d.status !== "COMPLETED" && d.days_left !== null) {
    const n = Math.abs(d.days_left);
    label = d.days_left < 0 ? `${n} day${n === 1 ? "" : "s"} overdue` : d.days_left === 0 ? "Due today" : `${n} day${n === 1 ? "" : "s"} left`;
  }
  return <Chip size="small" label={label} style={{ backgroundColor: s.tint, color: s.color, fontWeight: 600 }} />;
}

function Tile({ status, count, active, onClick }) {
  const s = STATUS[status];
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`text-left rounded-xl border bg-white px-4 py-3 cursor-pointer transition-shadow hover:shadow-md ${
        active ? "ring-2 ring-offset-1" : ""
      }`}
      style={{ borderColor: active ? s.color : "#dbe4f0", ["--tw-ring-color"]: s.color }}
    >
      <div className="text-[12px] font-bold uppercase tracking-[0.06em]" style={{ color: s.color }}>
        {s.label}
      </div>
      <div className="text-[28px] font-semibold leading-tight text-[#0d1b36]">{count}</div>
    </button>
  );
}

function DeadlinesList() {
  const { data: user } = useSelector((s) => s.user);
  const errorHandler = useErrorHandler();
  const canAct = /^(admin|registrar)$/i.test((user.roles || user.role || ""));

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState(null);
  const [tick, setTick] = useState(0);
  const [adding, setAdding] = useState(false);
  const [completing, setCompleting] = useState(null);
  const [extending, setExtending] = useState(null);
  const [auditing, setAuditing] = useState(null);

  useEffect(() => {
    if (!user.token) return undefined;
    let cancelled = false;
    setLoading(true);
    getDeadlines(user.token)
      .then(({ data }) => !cancelled && setRows(data.data))
      .catch((err) => !cancelled && (setRows([]), errorHandler(err)))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [user.token, tick]);

  const counts = useMemo(() => {
    const c = { OVERDUE: 0, DUE_SOON: 0, ON_TRACK: 0, COMPLETED: 0 };
    rows.forEach((r) => (c[r.status] = (c[r.status] || 0) + 1));
    return c;
  }, [rows]);

  const visible = useMemo(
    () => rows.filter((r) => !filter || r.status === filter).sort((a, b) => String(b.created_at).localeCompare(String(a.created_at)) || b.id - a.id), // newest first
    [rows, filter]
  );

  const refresh = () => setTick((t) => t + 1);

  return (
    <Box className="px-4 pb-6">
      <Box className="grid grid-cols-2 min-[900px]:grid-cols-4 gap-3 mb-4">
        {Object.keys(STATUS).map((k) => (
          <Tile key={k} status={k} count={counts[k]} active={filter === k} onClick={() => setFilter(filter === k ? null : k)} />
        ))}
      </Box>

      <Box className="flex flex-wrap items-center justify-between gap-3 mb-3 min-h-[32px]">
        <Typography className="text-[13px] text-[#6b7a90]">
          {filter ? `Showing ${STATUS[filter].label.toLowerCase()} deadlines` : "Showing all deadlines"}
          {filter ? (
            <button type="button" onClick={() => setFilter(null)} className="ml-2 p-0 text-[13px] font-semibold text-brand bg-transparent border-0 cursor-pointer">
              Clear filter
            </button>
          ) : null}
        </Typography>
        {canAct ? (
          <Button variant="contained" size="small" onClick={() => setAdding(true)}>
            Add deadline
          </Button>
        ) : null}
      </Box>

      {loading ? (
        <Box className="py-16 grid place-items-center">
          <CircularProgress size={28} />
        </Box>
      ) : !visible.length ? (
        <Box className="py-16 text-center text-[#6b7a90]">
          <AlarmOutlined className="!text-[48px] opacity-40" />
          <Typography className="mt-2">{rows.length ? "No deadlines match this filter." : "No deadlines yet."}</Typography>
        </Box>
      ) : (
        <>
        <ul className="m-0 flex list-none flex-col gap-3 p-0 min-[900px]:hidden">
          {visible.map((d) => (
            <li key={d.id}>
              <Paper variant="outlined" className="rounded-xl px-4 py-3">
                <Box className="flex flex-wrap items-start justify-between gap-2">
                  <Box className="min-w-0">
                    <div className="text-[14px] font-semibold text-[#0d1b36]">{d.name}</div>
                    {d.stage ? <div className="text-[12px] text-[#6b7a90]">{d.stage}</div> : null}
                  </Box>
                  <Box className="flex flex-wrap items-center gap-1.5">
                    {daysChip(d)}
                    {d.escalated_at ? (
                      <Chip size="small" variant="outlined" label="Escalated" className="!border-[#c62828] !text-[#c62828]" />
                    ) : null}
                  </Box>
                </Box>
                <Link to={`/timeline/${d.case_id}`} className="mt-1 block text-[13px] font-semibold text-brand no-underline hover:underline">
                  {d.case_title}
                </Link>
                <div className="text-[12px] text-[#6b7a90]">Due {fmtDate(d.due_date)}</div>
                <Box className="-ml-2 mt-1 flex flex-wrap">
                  {canAct && d.status !== "COMPLETED" ? (
                    <>
                      <Button size="small" onClick={() => setCompleting(d)}>
                        Complete
                      </Button>
                      <Button size="small" onClick={() => setExtending(d)}>
                        Extend
                      </Button>
                    </>
                  ) : null}
                  <Button size="small" startIcon={<HistoryOutlined fontSize="small" />} onClick={() => setAuditing(d)}>
                    History
                  </Button>
                </Box>
              </Paper>
            </li>
          ))}
        </ul>
        <Paper variant="outlined" className="hidden rounded-xl overflow-x-auto min-[900px]:block">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Case</TableCell>
                <TableCell>Deadline</TableCell>
                <TableCell>Due</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visible.map((d) => (
                <TableRow key={d.id} hover>
                  <TableCell>
                    <Link to={`/timeline/${d.case_id}`} className="font-semibold text-[13px] text-brand no-underline hover:underline">
                      {d.case_title}
                    </Link>
                  </TableCell>
                  <TableCell>
                    <div className="font-semibold text-[13px] text-[#0d1b36]">{d.name}</div>
                    {d.stage ? <div className="text-[12px] text-[#6b7a90]">{d.stage}</div> : null}
                  </TableCell>
                  <TableCell className="whitespace-nowrap">{fmtDate(d.due_date)}</TableCell>
                  <TableCell>
                    <Box className="flex flex-wrap items-center gap-1.5">
                      {daysChip(d)}
                      {d.escalated_at ? (
                        <Tooltip title={`Escalated ${fmtDate(d.escalated_at)}`}>
                          <Chip size="small" variant="outlined" label="Escalated" className="!border-[#c62828] !text-[#c62828]" />
                        </Tooltip>
                      ) : null}
                    </Box>
                  </TableCell>
                  <TableCell align="right" className="min-[900px]:whitespace-nowrap">
                    {canAct && d.status !== "COMPLETED" ? (
                      <>
                        <Button size="small" onClick={() => setCompleting(d)}>
                          Complete
                        </Button>
                        <Button size="small" onClick={() => setExtending(d)}>
                          Extend
                        </Button>
                      </>
                    ) : null}
                    <Button size="small" startIcon={<HistoryOutlined fontSize="small" />} onClick={() => setAuditing(d)}>
                      History
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
        </>
      )}

      <AddDeadlineDialog
        open={adding}
        onClose={() => setAdding(false)}
        onDone={() => {
          setAdding(false);
          refresh();
        }}
      />
      <CompleteDialog
        deadline={completing}
        onClose={() => setCompleting(null)}
        onDone={() => {
          setCompleting(null);
          refresh();
        }}
      />
      <ExtendDialog
        deadline={extending}
        onClose={() => setExtending(null)}
        onDone={() => {
          setExtending(null);
          refresh();
        }}
      />
      <AuditDialog deadline={auditing} onClose={() => setAuditing(null)} />
    </Box>
  );
}

export default function Deadlines() {
  const { data: user } = useSelector((s) => s.user);
  const isAdmin = /^admin$/i.test((user.roles || user.role || ""));
  const [tab, setTab] = useState("deadlines");

  return (
    <Paper className="odr-card">
      <Box className="odr-card-header flex flex-wrap items-center justify-between gap-3">
        <Typography className="heading-title">Deadlines</Typography>
      </Box>
      {isAdmin ? (
        <Tabs value={tab} onChange={(_, v) => setTab(v)} className="px-4 mb-3">
          <Tab value="deadlines" label="Deadlines" />
          <Tab value="rules" label="Rules & holidays" />
        </Tabs>
      ) : null}
      {tab === "rules" && isAdmin ? <RulesAndHolidays /> : <DeadlinesList />}
    </Paper>
  );
}
